import React, { useEffect, useState } from 'react';
import { getUsers, createUser, resetUserPassword, updateUserRole, deleteUser, changePassword } from '../api';
import api from '../api';

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
      <span style={{ fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle = {
  padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)',
  fontSize: 13, width: '100%', boxSizing: 'border-box',
};

function NotifPrefs({ userId, showAdvanced = false }) {
  const PREFS = [
    { key: 'notif_bank_sync',      label: 'Sync bancaire' },
    { key: 'notif_uncategorized',  label: 'Transactions à catégoriser' },
    { key: 'notif_budget_alert',   label: 'Alertes budget' },
    { key: 'notif_weekly_summary', label: 'Résumé hebdo' },
    { key: 'notif_balance_alert',  label: 'Alerte solde faible' },
  ];
  const [prefs, setPrefs] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api.get(`/auth/users/${userId}/notif-prefs`).then(r => setPrefs(r.data)).catch(() => {});
  }, [userId]);

  const save = async (patch) => {
    try {
      const r = await api.patch(`/auth/users/${userId}/notif-prefs`, patch);
      setPrefs(r.data);
      setMsg({ ok: true, text: 'Enregistré.' });
      setTimeout(() => setMsg(null), 2000);
    } catch { setMsg({ ok: false, text: 'Erreur' }); }
  };

  if (!prefs) return null;

  return (
    <div style={{ marginTop: 20, padding: '16px 18px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)' }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 14 }}>Notifications</div>

      {showAdvanced && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '8px 12px', alignItems: 'center', marginBottom: 14 }}>
          <label style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 600 }}>Topic ntfy</label>
          <input
            defaultValue={prefs.ntfy_topic || ''}
            onBlur={e => save({ ntfy_topic: e.target.value || null })}
            placeholder="budget-ernesto"
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, width: 160 }}
          />
          <label style={{ fontSize: 12, color: 'var(--text-soft)', fontWeight: 600 }}>4 derniers chiffres de la carte</label>
          <input
            defaultValue={prefs.card_last4 || ''}
            onBlur={e => save({ card_last4: e.target.value || null })}
            placeholder="1234"
            maxLength={4}
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', fontSize: 12, width: 160 }}
          />
        </div>
      )}

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>Types de notifications reçues :</div>
      {PREFS.map(p => (
        <label key={p.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={prefs[p.key] ?? true}
            onChange={e => save({ [p.key]: e.target.checked })}
          />
          <span style={{ fontSize: 13, color: 'var(--text-soft)' }}>{p.label}</span>
        </label>
      ))}
      {msg && <div style={{ fontSize: 12, marginTop: 6, color: msg.ok ? 'var(--success)' : 'var(--danger)' }}>{msg.text}</div>}
    </div>
  );
}

export default function UserSettings({ currentUser }) {
  const isAdmin = currentUser?.role === 'admin';

  // ── Changer mon mot de passe ──────────────────────────
  const [pwForm,    setPwForm]    = useState({ current: '', next: '', confirm: '' });
  const [pwMsg,     setPwMsg]     = useState(null);
  const [pwLoading, setPwLoading] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm)
      return setPwMsg({ ok: false, text: 'Les mots de passe ne correspondent pas.' });
    if (pwForm.next.length < 6)
      return setPwMsg({ ok: false, text: 'Minimum 6 caractères.' });
    setPwLoading(true);
    try {
      await changePassword(pwForm.current, pwForm.next);
      setPwMsg({ ok: true, text: 'Mot de passe modifié.' });
      setPwForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setPwMsg({ ok: false, text: err.response?.data?.error || 'Erreur' });
    } finally {
      setPwLoading(false);
    }
  };

  // ── Gestion des utilisateurs (admin) ──────────────────
  const [users,      setUsers]      = useState([]);
  const [newUser,    setNewUser]    = useState({ firstName: '', email: '', password: '', role: 'user' });
  const [newUserMsg, setNewUserMsg] = useState(null);
  const [resetPw,    setResetPw]    = useState({}); // { [userId]: newPw }
  const [resetMsg,   setResetMsg]   = useState({});

  const loadUsers = async () => {
    try { const r = await getUsers(); setUsers(r.data); } catch {}
  };

  useEffect(() => { if (isAdmin) loadUsers(); }, [isAdmin]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await createUser(newUser);
      setNewUserMsg({ ok: true, text: 'Utilisateur créé.' });
      setNewUser({ firstName: '', email: '', password: '', role: 'user' });
      loadUsers();
    } catch (err) {
      setNewUserMsg({ ok: false, text: err.response?.data?.error || 'Erreur' });
    }
  };

  const handleResetPassword = async (userId) => {
    const pw = resetPw[userId];
    if (!pw || pw.length < 6) {
      setResetMsg(m => ({ ...m, [userId]: { ok: false, text: 'Min. 6 caractères' } }));
      return;
    }
    try {
      await resetUserPassword(userId, pw);
      setResetMsg(m => ({ ...m, [userId]: { ok: true, text: 'Mot de passe réinitialisé.' } }));
      setResetPw(p => ({ ...p, [userId]: '' }));
    } catch (err) {
      setResetMsg(m => ({ ...m, [userId]: { ok: false, text: err.response?.data?.error || 'Erreur' } }));
    }
  };

  const handleToggleRole = async (user) => {
    const newRole = user.role === 'admin' ? 'user' : 'admin';
    await updateUserRole(user.id, newRole);
    loadUsers();
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Désactiver ${user.first_name} ? (son compte sera verrouillé)`)) return;
    await deleteUser(user.id);
    loadUsers();
  };

  const msgStyle = (ok) => ({
    padding: '8px 12px', borderRadius: 7, fontSize: 13, marginTop: 8,
    background: ok ? '#f0faf4' : '#fff3f3',
    color: ok ? '#2e7d32' : '#d32f2f',
    border: `1px solid ${ok ? '#a5d6a7' : '#ffcdd2'}`,
  });

  return (
    <div style={{ marginTop: 40, maxWidth: 560 }}>
      <h2>Compte &amp; utilisateurs</h2>

      {/* ── MES NOTIFICATIONS ── */}
      {currentUser?.id && (
        <div style={{ marginBottom: 40 }}>
          <h3 style={{ marginBottom: 4 }}>Mes notifications</h3>
          <NotifPrefs userId={currentUser.id} showAdvanced={isAdmin} />
        </div>
      )}

      {/* ── MON MOT DE PASSE ── */}
      <div style={{ marginBottom: 40 }}>
        <h3 style={{ marginBottom: 16 }}>Changer mon mot de passe</h3>
        <form onSubmit={handleChangePassword} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Mot de passe actuel">
            <input type="password" value={pwForm.current} required style={inputStyle}
              onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} />
          </Field>
          <Field label="Nouveau mot de passe">
            <input type="password" value={pwForm.next} required style={inputStyle}
              onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} />
          </Field>
          <Field label="Confirmer le nouveau mot de passe">
            <input type="password" value={pwForm.confirm} required style={inputStyle}
              onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} />
          </Field>
          {pwMsg && <div style={msgStyle(pwMsg.ok)}>{pwMsg.text}</div>}
          <button type="submit" disabled={pwLoading} style={{
            alignSelf: 'flex-start', padding: '8px 20px', borderRadius: 7,
            border: 'none', background: 'var(--primary)', color: '#fff',
            fontWeight: 600, cursor: 'pointer', fontSize: 13,
          }}>
            {pwLoading ? 'Enregistrement…' : 'Modifier'}
          </button>
        </form>
      </div>

      {/* ── GESTION UTILISATEURS (admin) ── */}
      {isAdmin && (
        <>
          <h3 style={{ marginBottom: 16 }}>Utilisateurs</h3>

          {/* Liste */}
          <div style={{ marginBottom: 32 }}>
            {users.map(u => (
              <div key={u.id} style={{
                padding: '14px 16px', marginBottom: 10,
                border: '1px solid var(--border)', borderRadius: 10,
                background: 'var(--surface)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div>
                    <span style={{ fontWeight: 600 }}>{u.first_name}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>{u.email}</span>
                    <span style={{
                      marginLeft: 8, fontSize: 11, padding: '2px 7px', borderRadius: 10, fontWeight: 600,
                      background: u.role === 'admin' ? '#e3f2fd' : '#f5f5f5',
                      color: u.role === 'admin' ? '#1565c0' : '#666',
                    }}>{u.role}</span>
                    {!u.has_password && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--danger)' }}>⚠ compte verrouillé</span>
                    )}
                  </div>
                  {u.id !== currentUser.id && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => handleToggleRole(u)} style={{
                        padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                        border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-soft)',
                      }}>
                        {u.role === 'admin' ? '→ user' : '→ admin'}
                      </button>
                      <button onClick={() => handleDelete(u)} style={{
                        padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                        border: '1px solid #ffcdd2', background: '#fff5f5', color: 'var(--danger)',
                      }}>Verrouiller</button>
                    </div>
                  )}
                </div>

                {/* Reset mot de passe */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="password"
                    placeholder="Nouveau mot de passe…"
                    value={resetPw[u.id] || ''}
                    onChange={e => setResetPw(p => ({ ...p, [u.id]: e.target.value }))}
                    style={{ ...inputStyle, flex: 1, padding: '6px 10px' }}
                  />
                  <button onClick={() => handleResetPassword(u.id)} style={{
                    padding: '6px 14px', borderRadius: 7, fontSize: 12, cursor: 'pointer',
                    border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 600, whiteSpace: 'nowrap',
                  }}>Réinitialiser</button>
                </div>
                {resetMsg[u.id] && <div style={msgStyle(resetMsg[u.id].ok)}>{resetMsg[u.id].text}</div>}
                {u.id !== currentUser.id && <NotifPrefs userId={u.id} showAdvanced={true} />}
              </div>
            ))}
          </div>

          {/* Créer un utilisateur */}
          <h3 style={{ marginBottom: 16 }}>Ajouter un utilisateur</h3>
          <form onSubmit={handleCreateUser} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Prénom">
                <input type="text" value={newUser.firstName} required style={inputStyle}
                  onChange={e => setNewUser(u => ({ ...u, firstName: e.target.value }))} />
              </Field>
              <Field label="Rôle">
                <select value={newUser.role} style={inputStyle}
                  onChange={e => setNewUser(u => ({ ...u, role: e.target.value }))}>
                  <option value="user">Utilisateur</option>
                  <option value="admin">Admin</option>
                </select>
              </Field>
            </div>
            <Field label="Email">
              <input type="email" value={newUser.email} required style={inputStyle}
                onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} />
            </Field>
            <Field label="Mot de passe (min. 6 caractères)">
              <input type="password" value={newUser.password} required style={inputStyle}
                onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} />
            </Field>
            {newUserMsg && <div style={msgStyle(newUserMsg.ok)}>{newUserMsg.text}</div>}
            <button type="submit" style={{
              alignSelf: 'flex-start', padding: '8px 20px', borderRadius: 7,
              border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 13,
            }}>Créer</button>
          </form>
        </>
      )}
    </div>
  );
}
